import React, { useEffect, useState, useMemo } from "react";
import WpDynamicTable from "../../Components/WhatsappTable/WpDynamicTable";
import { LuLayoutGrid, LuCopy, LuCheck } from "react-icons/lu";
import { useDispatch, useSelector } from "react-redux";
import {
  DeleteTemplate,
  fetchTemplates,
  UpdateTemplate,
} from "../../Redux/WhatsappSlice/Template";
import { toast } from "react-toastify";
import Loader from "../../Components/Loader/Loader";

const formatSQL = (sql) => {
  if (!sql || typeof sql !== "string") return sql;
  
  const keywords = [
    "SELECT", "FROM", "WHERE", "AND", "OR", "GROUP BY", "ORDER BY", 
    "LEFT JOIN", "RIGHT JOIN", "INNER JOIN", "JOIN", "ON", 
    "INSERT INTO", "VALUES", "UPDATE", "SET", "DECLARE"
  ];
  
  let formatted = sql;
  keywords.forEach(keyword => {
    const regex = new RegExp(`\\b${keyword}\\b`, 'gi');
    formatted = formatted.replace(regex, `\n${keyword}`);
  });
  
  return formatted
    .split('\n')
    .map(line => line.trim())
    .filter(line => line.length > 0)
    .join('\n');
};

const ResponseCell = ({ text: rawText, limit = 50, isCode = false, isAllExpanded = false }) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const [copied, setCopied] = useState(false);
    const [isFormatted, setIsFormatted] = useState(false);

    const text = typeof rawText === 'object' && rawText !== null 
        ? JSON.stringify(rawText) 
        : (rawText || "-");

    const formattedJSON = useMemo(() => {
        if (!text || text === "-") return null;
        
        // 1. Try standard single JSON object/array parse
        try {
            const parsed = JSON.parse(text);
            if (parsed && typeof parsed === "object") {
                return JSON.stringify(parsed, null, 2);
            }
        } catch (e) {}

        // 2. Try single JSON parse with single-to-double quote substitution
        try {
            const normalized = text.replace(/'/g, '"');
            const parsed = JSON.parse(normalized);
            if (parsed && typeof parsed === "object") {
                return JSON.stringify(parsed, null, 2);
            }
        } catch (e) {}

        // 3. Try parsing concatenated JSON objects ({...}{...})
        try {
            const results = [];
            let braceCount = 0;
            let startIndex = -1;

            for (let i = 0; i < text.length; i++) {
                const char = text[i];
                if (char === '{') {
                    if (braceCount === 0) {
                        startIndex = i;
                    }
                    braceCount++;
                } else if (char === '}') {
                    braceCount--;
                    if (braceCount === 0 && startIndex !== -1) {
                        const jsonStr = text.substring(startIndex, i + 1);
                        try {
                            results.push(JSON.parse(jsonStr));
                        } catch (e1) {
                            try {
                                const normalized = jsonStr.replace(/'/g, '"');
                                results.push(JSON.parse(normalized));
                            } catch (e2) {}
                        }
                        startIndex = -1;
                    }
                }
            }

            if (results.length > 0) {
                if (results.length === 1) {
                    return JSON.stringify(results[0], null, 2);
                }
                return JSON.stringify(results, null, 2);
            }
        } catch (e) {}

        return null;
    }, [text]);

    const formattedSQL = useMemo(() => {
        if (!isCode || !text || text === "-") return null;
        return formatSQL(text);
    }, [text, isCode]);

    if (!text || text === "-") return "-";

    const activeText = isFormatted 
        ? (isCode ? (formattedSQL || text) : (formattedJSON || text)) 
        : text;

    const handleCopy = async (e) => {
        e.stopPropagation();
        try {
            await navigator.clipboard.writeText(activeText);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch (err) {
            console.error("Failed to copy text: ", err);
        }
    };

    const charLimit = limit;
    const currentlyExpanded = isAllExpanded || isExpanded;
    const isTruncated = activeText.length > charLimit;
    const displayText = isTruncated ? (currentlyExpanded ? activeText : activeText.slice(0, charLimit) + "...") : activeText;

    return (
        <div className="text-left font-medium text-slate-700 leading-normal break-words relative group min-h-[24px] flex items-start pr-16 w-full py-1">
            <div className="w-full">
                {(isFormatted && ((isCode && formattedSQL) || (!isCode && formattedJSON))) || isCode ? (
                    <pre className={`bg-slate-50 border border-slate-200 rounded p-2 text-xs font-mono text-slate-800 overflow-x-auto whitespace-pre-wrap break-all scrollbar-thin ${currentlyExpanded ? "max-h-none overflow-y-visible" : "max-h-[300px] overflow-y-auto"}`}>
                        {displayText}
                    </pre>
                ) : (
                    <div className={currentlyExpanded ? "whitespace-normal break-all text-slate-700 font-medium py-0.5" : "overflow-x-auto whitespace-nowrap scrollbar-thin max-w-full text-slate-700 font-medium py-0.5"}>
                        {displayText}
                    </div>
                )}
                {isTruncated && (
                    <button
                        onClick={(e) => {
                            e.stopPropagation();
                            setIsExpanded(!isExpanded);
                        }}
                        className="text-xs text-blue-600 hover:text-blue-800 font-bold ml-1.5 focus:outline-none cursor-pointer border-none bg-transparent inline-flex items-center"
                    >
                        {currentlyExpanded ? "Read Less" : "Read More"}
                    </button>
                )}
            </div>
            
            <div className="absolute right-0 top-1 flex items-center gap-1 opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity">
                {((formattedJSON && !isCode) || (formattedSQL && isCode)) && (
                    <button
                        onClick={(e) => {
                            e.stopPropagation();
                            setIsFormatted(!isFormatted);
                        }}
                        className={`px-1.5 py-1 rounded hover:bg-slate-100 transition-all text-[10px] font-extrabold border border-slate-300 bg-white cursor-pointer flex items-center justify-center shadow-sm ${
                            isFormatted ? "text-blue-600 border-blue-400 bg-blue-50" : "text-slate-500 hover:text-slate-700"
                        }`}
                        title={isFormatted ? "Show raw text" : (isCode ? "Format SQL Script" : "Format JSON")}
                    >
                        {isCode ? "SQL" : "JSON"}
                    </button>
                )}
                <button
                    onClick={handleCopy}
                    className="p-1.5 rounded hover:bg-slate-100 transition-all text-slate-400 hover:text-slate-700 focus:outline-none cursor-pointer border-none bg-transparent flex items-center justify-center"
                    title="Copy to clipboard"
                >
                    {copied ? (
                        <LuCheck className="text-green-600" size={15} />
                    ) : (
                        <LuCopy size={15} />
                    )}
                </button>
            </div>
        </div>
    );
};

const View_Template = () => {
  const dispatch = useDispatch();

  const { TemplateData, templateLoading } = useSelector((state) => state.Template);

  const [formatIdSearch, setFormatIdSearch] = useState("");
  const [templateNameSearch, setTemplateNameSearch] = useState("");
  const [filteredRows, setFilteredRows] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [expandAll, setExpandAll] = useState(false);

  const [formData, setFormData] = useState({
    FormatID: "",
    Script: "",
    TemplateName: "",
    TemplateJson: "",
  });

  useEffect(() => {
    dispatch(fetchTemplates());
  }, [dispatch]);

  const handleClearFilters = () => {
    setFormatIdSearch("");
    setTemplateNameSearch("");
  };

  const handleEdit = (row) => {
    setShowModal(true);
    setEditingId(row.MainId);
    setFormData({
      FormatID: row.FormatID || "",
      Script: row.Script || "",
      TemplateName: row.TemplateName || "",
      TemplateJson: row.TemplateJson || "",
    });
  };

  const handleDelete = async (row) => {
    const isConfirmed = window.confirm(
      `Are you sure you want to delete this Template?`
    );
    if (!isConfirmed) return;

    try {
      await dispatch(DeleteTemplate({ id: row.MainId })).unwrap();
      toast.success("Template deleted successfully!", {
        position: "top-right",
        autoClose: 1000,
        hideProgressBar: false,
        closeOnClick: false,
        pauseOnHover: true,
      });
      dispatch(fetchTemplates()); // refresh list
    } catch (error) {
      toast.error(`Failed to delete Template: ${error}`, {
        position: "top-right",
        autoClose: 1000,
        hideProgressBar: false,
        closeOnClick: false,
        pauseOnHover: true,
      });
    }
  };

  const handleUpdate = async () => {
    try {
      await dispatch(
        UpdateTemplate({
          id: editingId,
          updatedData: formData,
        })
      ).unwrap();
      toast.success("Template updated successfully!");
      setShowModal(false);
      dispatch(fetchTemplates());
    } catch (error) {
      toast.error("Failed to update template.");
    }
  };

  useEffect(() => {
    if (TemplateData?.length > 0) {
      const filtered = TemplateData?.filter((el) => {
        const formatMatch = el?.FormatID?.toString()
          .toLowerCase()
          .includes(formatIdSearch.toLowerCase());
        const nameMatch = el?.TemplateName?.toLowerCase()?.includes(
          templateNameSearch.toLowerCase()
        );
        return formatMatch && nameMatch;
      });

      const mapped = filtered?.map((el, index) => ({
        id: index + 1,
        MainId: el._id,
        FormatID: el?.FormatID,
        Script: el?.Script,
        TemplateName: el?.TemplateName,
        TemplateJson: el?.TemplateJson,
      }));

      setFilteredRows(mapped);
    } else {
      setFilteredRows([]);
    }
  }, [TemplateData, formatIdSearch, templateNameSearch]);

  const columns = [
    { field: "id", headerName: "No", flex: 0.3, minWidth: 50 },
    { field: "FormatID", headerName: "Format ID", flex: 0.4, minWidth: 80 },
    { 
      field: "Script", 
      headerName: "Script", 
      flex: 3,
      minWidth: 500,
      renderCell: ({ row }) => <ResponseCell text={row.Script} limit={30} isCode={true} isAllExpanded={expandAll} />
    },
    { field: "TemplateName", headerName: "Template Name", flex: 1, minWidth: 150 },
    {
      field: "TemplateJson",
      headerName: "Template JSON",
      flex: 2,
      minWidth: 350,
      renderCell: ({ row }) => <ResponseCell text={row.TemplateJson} limit={30} isAllExpanded={expandAll} />
    },
    {
      field: "actions",
      headerName: "Actions",
      flex: 1,
      minWidth: 180,
      renderCell: (params) => (
        <div className="flex space-x-2 justify-center">
          <button
            className="bg-blue-500 text-white px-3 py-1 rounded hover:bg-blue-600"
            onClick={() => handleEdit(params.row)}
          >
            Update
          </button>
          <button
            className="bg-red-500 text-white px-3 py-1 rounded hover:bg-red-600"
            onClick={() => handleDelete(params.row)}
          >
            Delete
          </button>
        </div>
      ),
    },
  ];

  // const rows =
  //   TemplateData?.data?.map((el, index) => ({
  //     id: index + 1,
  //     MainId: el._id,
  //     FormatID: el?.FormatID,
  //     Script: el?.Script,
  //     TemplateName: el?.TemplateName,
  //     TemplateJson: el?.TemplateJson,
  //   })) || [];

  if (
    !TemplateData || templateLoading
  ) {
    return <Loader />;
  }

  return (
    <>
      <div className="">
        <div className="title flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
          {/* Title */}
          <h3 className="text-xl font-bold main_title_page text-white px-5 py-2 rounded w-full sm:w-auto text-center sm:text-left">
            View Template
          </h3>

          {/* Action Buttons */}
          <div className="flex items-center space-x-2 bg-[#122A47] text-white px-4 py-2 rounded-lg shadow-md self-end sm:self-auto">
            <span className="text-sm font-semibold">Expand All</span>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={expandAll}
                onChange={(e) => setExpandAll(e.target.checked)}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-600 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-500"></div>
            </label>
          </div>
        </div>
        <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 mt-4 w-full sm:w-auto">
          <input
            type="text"
            placeholder="Search Format ID"
            value={formatIdSearch}
            onChange={(e) => setFormatIdSearch(e.target.value)}
            className="w-full border border-gray-300 rounded px-3 py-2 text-sm md:w-48"
          />
          <select
            value={templateNameSearch}
            onChange={(e) => setTemplateNameSearch(e.target.value)}
            className="w-full border border-gray-300 rounded px-3 py-2 text-sm md:w-60"
          >
            <option value="">Select Template Name</option>
            {[
              ...new Set(
                (Array.isArray(TemplateData) ? TemplateData : []).map(
                  (el) => el?.TemplateName
                )
              ),
            ]
              ?.filter(Boolean)
              ?.map((name, idx) => (
                <option key={idx} value={name}>
                  {name}
                </option>
              ))}
          </select>

          <button
            onClick={handleClearFilters}
            className="bg-blue-600 text-white px-3 py-2 rounded hover:bg-blue-700 flex items-center justify-center"
          >
            <LuLayoutGrid size={18} className="me-1" />
            Show All Log
          </button>
        </div>

        <div className="bg-transparent rounded-lg overflow-x-auto mt-5">
          {/* <DynamicTable columns={columns} rows={rows} /> */}
          <WpDynamicTable
            columns={columns}
            rows={filteredRows}
            height="600px"
          />
        </div>

        {/* ==== Modal for Edit ==== */}
        {showModal && (
          <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50 p-2">
            <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-lg">
              <h2 className="text-lg font-semibold mb-4">Update Template</h2>
              <div className="grid gap-4">
                <input
                  type="text"
                  placeholder="Format ID"
                  value={formData.FormatID}
                  onChange={(e) =>
                    setFormData({ ...formData, FormatID: e.target.value })
                  }
                  className="border border-gray-300 rounded px-3 py-2"
                />
                <textarea
                  placeholder="Script"
                  value={formData.Script}
                  onChange={(e) =>
                    setFormData({ ...formData, Script: e.target.value })
                  }
                  className="border border-gray-300 rounded px-3 py-2"
                />
                <input
                  type="text"
                  placeholder="Template Name"
                  value={formData.TemplateName}
                  onChange={(e) =>
                    setFormData({ ...formData, TemplateName: e.target.value })
                  }
                  className="border border-gray-300 rounded px-3 py-2"
                />
                <textarea
                  placeholder="Template JSON"
                  value={formData.TemplateJson}
                  onChange={(e) =>
                    setFormData({ ...formData, TemplateJson: e.target.value })
                  }
                  className="border border-gray-300 rounded px-3 py-2"
                />
              </div>

              <div className="flex justify-end mt-4 gap-3">
                <button
                  onClick={() => setShowModal(false)}
                  className="bg-gray-500 text-white px-4 py-2 rounded"
                >
                  Cancel
                </button>
                <button
                  onClick={handleUpdate}
                  className="bg-green-600 text-white px-4 py-2 rounded"
                >
                  Update
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default View_Template;
